import React, { useState, useEffect } from 'react';

const AdminDashboard = () => {
  // Configuration
  const API_BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';
  
  // State Management
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentToken, setCurrentToken] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [personalStats, setPersonalStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [editAdminModal, setEditAdminModal] = useState({ open: false, data: null });
  const [editUserModal, setEditUserModal] = useState({ open: false, data: null });
  const [personalStatsModal, setPersonalStatsModal] = useState({ open: false });
  
  // Form states
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [userSearch, setUserSearch] = useState('');
  
  // Statistics
  const [stats, setStats] = useState({
    totalAdmins: 0,
    totalCheckouts: 0,
    todayCheckouts: 0,
    activeUsers: 0
  });

  // Admin Ranks Definition
  const ADMIN_RANKS = [
    { name: 'Beginner', minCheckouts: 0, maxCheckouts: 4, color: '#64748B', icon: '👤' },
    { name: 'Helper', minCheckouts: 5, maxCheckouts: 14, color: '#059669', icon: '🤝' },
    { name: 'Guardian', minCheckouts: 15, maxCheckouts: 29, color: '#0D9488', icon: '🛡️' },
    { name: 'Coordinator', minCheckouts: 30, maxCheckouts: 49, color: '#0284C7', icon: '📋' },
    { name: 'Supervisor', minCheckouts: 50, maxCheckouts: 99, color: '#7C3AED', icon: '👁️' },
    { name: 'Master', minCheckouts: 100, maxCheckouts: 199, color: '#DC2626', icon: '🎯' },
    { name: 'Legend', minCheckouts: 200, maxCheckouts: 999, color: '#B45309', icon: '👑' }
  ];

  // Utility Functions
  const getCurrentRank = (checkouts) => {
    return ADMIN_RANKS.find(rank => 
      checkouts >= rank.minCheckouts && checkouts <= rank.maxCheckouts
    ) || ADMIN_RANKS[0];
  };

  const showMessage = (message, type = 'success') => {
    if (type === 'success') {
      setSuccess(message);
      setError('');
    } else {
      setError(message);
      setSuccess('');
    }
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 5000);
  };

  // Check for existing session
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const user = localStorage.getItem('adminUser');
    
    if (token && user) {
      setCurrentToken(token);
      setCurrentUser(JSON.parse(user));
      setIsLoggedIn(true);
    }
  }, []);

  // Initialize dashboard when logged in
  useEffect(() => {
    if (isLoggedIn && currentToken) {
      initializeDashboard();
      
      // Auto-refresh every 30 seconds
      const interval = setInterval(() => {
        refreshAll();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, currentToken]);

  // Authentication
  const login = async () => {
    if (!loginForm.email || !loginForm.password) {
      showMessage('Please fill in all fields', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });

      const data = await response.json();

      if (response.ok && data.token) {
        setCurrentToken(data.token);
        setCurrentUser(data.user);
        
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.user));
        
        // Check admin privileges
        const profileResponse = await fetch(`${API_BASE_URL}/api/auth/get-profile`, {
          headers: { 'Authorization': `Bearer ${data.token}` }
        });
        
        const profileData = await profileResponse.json();
        
        if (profileData.user.role !== 'ROLE_ADMIN') {
          throw new Error('Access denied. Admin privileges required.');
        }
        
        setIsLoggedIn(true);
        showMessage('Successfully logged in!');
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (error) {
      showMessage(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setCurrentToken(null);
    setCurrentUser(null);
    setIsLoggedIn(false);
    setLoginForm({ email: '', password: '' });
  };

  // Dashboard Initialization
  const initializeDashboard = async () => {
    try {
      await Promise.all([
        refreshLeaderboard(),
        refreshUsers(),
        loadPersonalStats()
      ]);
    } catch (error) {
      console.error('Dashboard initialization error:', error);
      showMessage('Error loading dashboard data', 'error');
    }
  };

  // Leaderboard Management
  const refreshLeaderboard = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/leaderboard`, {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setLeaderboardData(data.leaderboard || []);
        updateStats(data.leaderboard || [], usersData);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      showMessage('Error loading leaderboard', 'error');
    }
  };

  // User Management
  const refreshUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/get-all-users`, {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
      
      const data = await response.json();
      
      if (data.users) {
        setUsersData(data.users);
        updateStats(leaderboardData, data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showMessage('Error loading users', 'error');
    }
  };

  // Personal Stats
  const loadPersonalStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/leaderboard/personal`, {
        headers: { 'Authorization': `Bearer ${currentToken}` }
      });
      
      const data = await response.json();
      
      if (data.success && data.personalStats) {
        setPersonalStats(data.personalStats);
      }
    } catch (error) {
      console.error('Error loading personal stats:', error);
    }
  };

  // Update Statistics
  const updateStats = (leaderboard, users) => {
    const totalAdmins = leaderboard.length;
    const totalCheckouts = leaderboard.reduce((sum, admin) => sum + (admin.totalCheckouts || 0), 0);
    const todayCheckouts = leaderboard.reduce((sum, admin) => sum + (admin.todayCheckouts || 0), 0);
    const activeUsers = users.filter(user => user.status === 'ACTIVE').length;
    
    setStats({ totalAdmins, totalCheckouts, todayCheckouts, activeUsers });
  };

  // Admin Edit Functions
  const saveAdminChanges = async (adminData) => {
    try {
      // Update checkout count
      await fetch(`${API_BASE_URL}/api/admin/leaderboard/update-count`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adminId: adminData.id,
          totalCheckouts: adminData.totalCheckouts,
          campId: null
        })
      });
      
      // Update participation
      await fetch(`${API_BASE_URL}/api/admin/leaderboard/participation`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adminId: adminData.id,
          isParticipating: adminData.isParticipating,
          campId: null
        })
      });
      
      setEditAdminModal({ open: false, data: null });
      await refreshLeaderboard();
      showMessage('Admin stats updated successfully!');
    } catch (error) {
      console.error('Error updating admin:', error);
      showMessage('Error updating admin stats', 'error');
    }
  };

  const resetAdminStats = async (adminId) => {
    if (!window.confirm('Are you sure you want to reset this admin\'s statistics? This action cannot be undone.')) {
      return;
    }
    
    try {
      await fetch(`${API_BASE_URL}/api/admin/leaderboard/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adminId: adminId,
          campId: null
        })
      });
      
      setEditAdminModal({ open: false, data: null });
      await refreshLeaderboard();
      showMessage('Admin stats reset successfully!');
    } catch (error) {
      console.error('Error resetting admin stats:', error);
      showMessage('Error resetting admin stats', 'error');
    }
  };

  // User Edit Functions
  const saveUserChanges = async (userData) => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/update-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: userData.email,
          status: userData.status
        })
      });
      
      setEditUserModal({ open: false, data: null });
      await refreshUsers();
      showMessage('User status updated successfully!');
    } catch (error) {
      console.error('Error updating user status:', error);
      showMessage('Error updating user status', 'error');
    }
  };

  // Personal Stats Functions
  const savePersonalStats = async (statsData) => {
    try {
      await fetch(`${API_BASE_URL}/api/admin/leaderboard/personal`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          totalCheckouts: statsData.totalCheckouts,
          isParticipating: statsData.isParticipating,
          campId: null
        })
      });
      
      setPersonalStatsModal({ open: false });
      await loadPersonalStats();
      await refreshLeaderboard();
      showMessage('Personal stats updated successfully!');
    } catch (error) {
      console.error('Error updating personal stats:', error);
      showMessage('Error updating personal stats', 'error');
    }
  };

  // Utility Functions
  const refreshAll = async () => {
    try {
      await Promise.all([
        refreshLeaderboard(),
        refreshUsers(),
        loadPersonalStats()
      ]);
      showMessage('All data refreshed successfully!');
    } catch (error) {
      console.error('Error refreshing data:', error);
      showMessage('Error refreshing data', 'error');
    }
  };

  const resetAllStats = async () => {
    if (!window.confirm('Are you sure you want to reset ALL admin statistics? This action cannot be undone.')) {
      return;
    }
    
    try {
      await fetch(`${API_BASE_URL}/api/admin/leaderboard/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adminId: null,
          campId: null
        })
      });
      
      await refreshAll();
      showMessage('All admin statistics have been reset!');
    } catch (error) {
      console.error('Error resetting all stats:', error);
      showMessage('Error resetting statistics', 'error');
    }
  };

  const exportLeaderboard = () => {
    const csvContent = [
      ['Rank', 'Name', 'Email', 'Level', 'Total Checkouts', 'Today Checkouts', 'Participating'].join(','),
      ...leaderboardData.map((admin, index) => {
        const rank = getCurrentRank(admin.totalCheckouts || 0);
        return [
          index + 1,
          admin.name || '',
          admin.email || '',
          rank.name,
          admin.totalCheckouts || 0,
          admin.todayCheckouts || 0,
          admin.isParticipating !== false ? 'Yes' : 'No'
        ].join(',');
      })
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-leaderboard-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    showMessage('Leaderboard exported to CSV!');
  };

  // Filter users based on search
  const filteredUsers = usersData.filter(user => 
    (user.username && user.username.toLowerCase().includes(userSearch.toLowerCase())) ||
    (user.email && user.email.toLowerCase().includes(userSearch.toLowerCase()))
  );

  // Login Form Component
  const LoginForm = () => (
    <div style={{
      maxWidth: '400px',
      margin: '50px auto',
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: '15px',
      padding: '40px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
    }}>
      <h2 style={{ textAlign: 'center', marginBottom: '30px', color: '#2d3748' }}>
        🚀 Admin Dashboard Login
      </h2>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#4a5568' }}>
          Email:
        </label>
        <input
          type="email"
          value={loginForm.email}
          onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
          placeholder="admin@example.com"
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '1rem'
          }}
          onKeyPress={(e) => e.key === 'Enter' && login()}
        />
      </div>
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#4a5568' }}>
          Password:
        </label>
        <input
          type="password"
          value={loginForm.password}
          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
          placeholder="Password"
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '1rem'
          }}
          onKeyPress={(e) => e.key === 'Enter' && login()}
        />
      </div>
      <button
        onClick={login}
        disabled={loading}
        style={{
          width: '100%',
          padding: '12px',
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '1rem',
          fontWeight: '600',
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.7 : 1
        }}
      >
        {loading ? 'Logging in...' : 'Login'}
      </button>
      {error && (
        <div style={{
          padding: '15px',
          borderRadius: '8px',
          marginTop: '15px',
          background: '#fed7d7',
          color: '#c53030',
          border: '1px solid #fc8181',
          fontWeight: '600'
        }}>
          {error}
        </div>
      )}
    </div>
  );

  // Main Dashboard Component
  const Dashboard = () => (
    <div style={{
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      color: '#333'
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '15px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div>
          <h1 style={{ color: '#2d3748', fontSize: '2.5rem', fontWeight: '700', marginBottom: '5px' }}>
            🏆 Admin Dashboard
          </h1>
          <p style={{ color: '#718096', fontSize: '1.1rem' }}>
            Leaderboard & User Management System
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '20px',
            fontWeight: '600',
            marginLeft: '10px'
          }}>
            {currentUser?.username || currentUser?.email}
          </span>
          <button
            onClick={logout}
            style={{
              padding: '8px 16px',
              background: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              marginLeft: '10px'
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Messages */}
      {success && (
        <div style={{
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '15px',
          background: '#c6f6d5',
          color: '#22543d',
          border: '1px solid #9ae6b4',
          fontWeight: '600'
        }}>
          {success}
        </div>
      )}
      
      {error && (
        <div style={{
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '15px',
          background: '#fed7d7',
          color: '#c53030',
          border: '1px solid #fc8181',
          fontWeight: '600'
        }}>
          {error}
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <button
          onClick={refreshAll}
          style={{
            background: 'linear-gradient(135deg, #4fd1c7, #06b6d4)',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          🔄 Refresh All Data
        </button>
        <button
          onClick={resetAllStats}
          style={{
            background: 'linear-gradient(135deg, #fc8181, #e53e3e)',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          ⚠️ Reset All Stats
        </button>
        <button
          onClick={() => setPersonalStatsModal({ open: true })}
          style={{
            background: 'linear-gradient(135deg, #68d391, #38a169)',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          ⚙️ Edit Personal Stats
        </button>
      </div>

      {/* Stats Overview */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '15px',
        padding: '25px',
        marginBottom: '20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{ color: '#2d3748', marginBottom: '20px', fontSize: '1.5rem' }}>
          📊 System Overview
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '15px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #4fd1c7, #06b6d4)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(79, 209, 199, 0.3)'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '5px' }}>
              {stats.totalAdmins}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: '0.9' }}>Total Admins</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #4fd1c7, #06b6d4)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(79, 209, 199, 0.3)'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '5px' }}>
              {stats.totalCheckouts}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: '0.9' }}>Total Checkouts</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #4fd1c7, #06b6d4)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(79, 209, 199, 0.3)'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '5px' }}>
              {stats.todayCheckouts}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: '0.9' }}>Today's Checkouts</div>
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #4fd1c7, #06b6d4)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 4px 15px rgba(79, 209, 199, 0.3)'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '5px' }}>
              {stats.activeUsers}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: '0.9' }}>Active Users</div>
          </div>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '20px',
        marginBottom: '20px'
      }}>
        {/* Admin Leaderboard */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '15px',
          padding: '25px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ color: '#2d3748', fontSize: '1.5rem', margin: 0 }}>🏆 Admin Leaderboard</h2>
            <div>
              <button
                onClick={refreshLeaderboard}
                style={{
                  padding: '8px 16px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginRight: '8px'
                }}
              >
                Refresh
              </button>
              <button
                onClick={exportLeaderboard}
                style={{
                  padding: '8px 16px',
                  background: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Export CSV
              </button>
            </div>
          </div>
          
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f7fafc', fontWeight: '600', color: '#4a5568' }}>Rank</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f7fafc', fontWeight: '600', color: '#4a5568' }}>Admin</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f7fafc', fontWeight: '600', color: '#4a5568' }}>Level</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f7fafc', fontWeight: '600', color: '#4a5568' }}>Total</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f7fafc', fontWeight: '600', color: '#4a5568' }}>Today</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', background: '#f7fafc', fontWeight: '600', color: '#4a5568' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardData.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e2e8f0' }}>
                      No admin data available
                    </td>
                  </tr>
                ) : (
                  leaderboardData.map((admin, index) => {
                    const rank = getCurrentRank(admin.totalCheckouts || 0);
                    return (
                      <tr key={admin.id}>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                          <strong>#{index + 1}</strong>
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #667eea, #764ba2)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: '600'
                            }}>
                              {admin.name ? admin.name.charAt(0).toUpperCase() : 'A'}
                            </div>
                            <div>
                              <div><strong>{admin.name || 'Unknown'}</strong></div>
                              <div style={{ fontSize: '0.8rem', color: '#718096' }}>
                                {admin.email || ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '4px 8px',
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            background: rank.name === 'Beginner' ? '#e2e8f0' : 
                                      rank.name === 'Helper' ? '#c6f6d5' :
                                      rank.name === 'Guardian' ? '#bee3f8' :
                                      rank.name === 'Coordinator' ? '#d6f5ff' :
                                      rank.name === 'Supervisor' ? '#e9d8fd' :
                                      rank.name === 'Master' ? '#fed7d7' : '#fef5e7',
                            color: rank.color
                          }}>
                            {rank.icon} {rank.name}
                          </span>
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                          <strong>{admin.totalCheckouts || 0}</strong>
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                          {admin.todayCheckouts || 0}
                        </td>
                        <td style={{ padding: '12px', borderBottom: '1px solid #e2e8f0' }}>
                          <button
                            onClick={() => setEditAdminModal({ 
                              open: true, 
                              data: {
                                id: admin.id,
                                name: admin.name,
                                totalCheckouts: admin.totalCheckouts || 0,
                                isParticipating: admin.isParticipating !== false
                              }
                            })}
                            style={{
                              padding: '8px 16px',
                              background: 'linear-gradient(135deg, #667eea, #764ba2)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* User Management */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '15px',
          padding: '25px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ color: '#2d3748', marginBottom: '20px', fontSize: '1.5rem' }}>
            👥 User Management
          </h2>
          <div style={{ marginBottom: '15px' }}>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search users..."
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '15px',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {filteredUsers.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#718096' }}>No users found</div>
            ) : (
              filteredUsers.map(user => (
                <div key={user.id} style={{
                  background: '#f7fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '15px',
                  transition: 'all 0.2s'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: user.status === 'ACTIVE' ? '#68d391' :
                                  user.status === 'CHECKEDOUT' ? '#4299e1' : '#a0aec0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: '600'
                    }}>
                      {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div>
                      <div><strong>{user.username || 'Unknown'}</strong></div>
                      <div style={{ fontSize: '0.9rem', color: '#718096' }}>
                        {user.email || ''}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#4a5568' }}>
                        Status: {user.status || 'Unknown'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setEditUserModal({
                      open: true,
                      data: {
                        id: user.id,
                        username: user.username,
                        email: user.email,
                        status: user.status || 'ACTIVE'
                      }
                    })}
                    style={{
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Edit Status
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Personal Stats Card */}
      {personalStats && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '15px',
          padding: '25px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ color: '#2d3748', marginBottom: '20px', fontSize: '1.5rem' }}>
            ⚙️ My Admin Stats
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '15px',
            marginBottom: '15px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #4fd1c7, #06b6d4)',
              color: 'white',
              padding: '20px',
              borderRadius: '12px',
              textAlign: 'center',
              boxShadow: '0 4px 15px rgba(79, 209, 199, 0.3)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '5px' }}>
                {personalStats.totalCheckouts || 0}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: '0.9' }}>Total Checkouts</div>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #4fd1c7, #06b6d4)',
              color: 'white',
              padding: '20px',
              borderRadius: '12px',
              textAlign: 'center',
              boxShadow: '0 4px 15px rgba(79, 209, 199, 0.3)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '5px' }}>
                {personalStats.todayCheckouts || 0}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: '0.9' }}>Today's Checkouts</div>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #4fd1c7, #06b6d4)',
              color: 'white',
              padding: '20px',
              borderRadius: '12px',
              textAlign: 'center',
              boxShadow: '0 4px 15px rgba(79, 209, 199, 0.3)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '5px' }}>
                {getCurrentRank(personalStats.totalCheckouts || 0).icon}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: '0.9' }}>
                {getCurrentRank(personalStats.totalCheckouts || 0).name}
              </div>
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #4fd1c7, #06b6d4)',
              color: 'white',
              padding: '20px',
              borderRadius: '12px',
              textAlign: 'center',
              boxShadow: '0 4px 15px rgba(79, 209, 199, 0.3)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '5px' }}>
                {personalStats.isParticipating !== false ? '✅' : '❌'}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: '0.9' }}>Participating</div>
            </div>
          </div>
          <button
            onClick={() => setPersonalStatsModal({ open: true })}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Edit My Stats
          </button>
        </div>
      )}
    </div>
  );

  // Modal Components
  const EditAdminModal = () => {
    const [formData, setFormData] = useState({
      totalCheckouts: editAdminModal.data?.totalCheckouts || 0,
      isParticipating: editAdminModal.data?.isParticipating || true
    });

    if (!editAdminModal.open) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(5px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '30px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          <h2 style={{ marginBottom: '20px' }}>Edit Admin Stats</h2>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#4a5568' }}>
              Admin Name:
            </label>
            <input
              type="text"
              value={editAdminModal.data?.name || ''}
              readOnly
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem',
                background: '#f7fafc'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#4a5568' }}>
              Total Checkouts:
            </label>
            <input
              type="number"
              value={formData.totalCheckouts}
              onChange={(e) => setFormData({ ...formData, totalCheckouts: parseInt(e.target.value) || 0 })}
              min="0"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#4a5568' }}>
              Participating:
            </label>
            <select
              value={formData.isParticipating.toString()}
              onChange={(e) => setFormData({ ...formData, isParticipating: e.target.value === 'true' })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button
              onClick={() => saveAdminChanges({ ...editAdminModal.data, ...formData })}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Save Changes
            </button>
            <button
              onClick={() => setEditAdminModal({ open: false, data: null })}
              style={{
                padding: '8px 16px',
                background: '#e2e8f0',
                color: '#4a5568',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => resetAdminStats(editAdminModal.data?.id)}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #fc8181, #e53e3e)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Reset Stats
            </button>
          </div>
        </div>
      </div>
    );
  };

  const EditUserModal = () => {
    const [formData, setFormData] = useState({
      status: editUserModal.data?.status || 'ACTIVE'
    });

    if (!editUserModal.open) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(5px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '30px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          <h2 style={{ marginBottom: '20px' }}>Edit User Status</h2>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#4a5568' }}>
              User:
            </label>
            <input
              type="text"
              value={`${editUserModal.data?.username} (${editUserModal.data?.email})`}
              readOnly
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem',
                background: '#f7fafc'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#4a5568' }}>
              Status:
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            >
              <option value="ACTIVE">Active</option>
              <option value="CHECKEDOUT">Checked Out</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
          
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button
              onClick={() => saveUserChanges({ ...editUserModal.data, ...formData })}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Save Changes
            </button>
            <button
              onClick={() => setEditUserModal({ open: false, data: null })}
              style={{
                padding: '8px 16px',
                background: '#e2e8f0',
                color: '#4a5568',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const PersonalStatsModal = () => {
    const [formData, setFormData] = useState({
      totalCheckouts: personalStats?.totalCheckouts || 0,
      isParticipating: personalStats?.isParticipating !== false
    });

    if (!personalStatsModal.open) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(5px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '30px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          <h2 style={{ marginBottom: '20px' }}>Edit My Admin Stats</h2>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#4a5568' }}>
              Total Checkouts:
            </label>
            <input
              type="number"
              value={formData.totalCheckouts}
              onChange={(e) => setFormData({ ...formData, totalCheckouts: parseInt(e.target.value) || 0 })}
              min="0"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#4a5568' }}>
              Participating in Leaderboard:
            </label>
            <select
              value={formData.isParticipating.toString()}
              onChange={(e) => setFormData({ ...formData, isParticipating: e.target.value === 'true' })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button
              onClick={() => savePersonalStats(formData)}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Save Changes
            </button>
            <button
              onClick={() => setPersonalStatsModal({ open: false })}
              style={{
                padding: '8px 16px',
                background: '#e2e8f0',
                color: '#4a5568',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Main Render
  return (
    <div style={{
      fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      color: '#333'
    }}>
      {!isLoggedIn ? <LoginForm /> : <Dashboard />}
      
      {/* Modals */}
      <EditAdminModal />
      <EditUserModal />
      <PersonalStatsModal />
    </div>
  );
};

export default AdminDashboard;